/** Every account-level read and write, keyed so one mutation invalidates exactly what it moved. */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { UseMutationResult, UseQueryResult } from "@tanstack/react-query";
import type {
  AccountProfile,
  AuthSessionSummary,
  HandleAvailability,
  Entitlement,
  Feed,
  LoadedSpace,
  MachineRecord,
  PairingView,
  Role,
  RoleAssignment,
  ScopeRef,
  SpacePage,
} from "@nebulize/sdk";

import { api, request } from "@/lib/api";
import { canvases, type PairingView as CanvasPairing } from "@/lib/canvases";
import { spaces } from "@/lib/spaces";

export type { CanvasPairing };

export const accountKeys = {
  canvasPairings: ["canvas", "pairings"] as const,
  me: ["account", "me"] as const,
  entitlement: ["account", "entitlement"] as const,
  machines: ["account", "machines"] as const,
  feeds: ["account", "feeds"] as const,
  discordChannels: (feedId: string) => ["account", "feeds", feedId, "discord-channels"] as const,
  sessions: ["account", "sessions"] as const,
  spaces: ["spaces", "page"] as const,
  loaded: ["spaces", "loaded"] as const,
  roles: (subjectId: string) => ["access", "roles", subjectId] as const,
  handleAvailability: (handle: string) => ["handles", handle, "availability"] as const,
};

/** `FeedListResponse`, an object rather than a bare array so it can gain a count later. */
interface FeedListResponse {
  feeds?: Feed[];
}

/** `RegisteredFeedResponse`. The secret is answered once and never again. */
export interface RegisteredFeed {
  feed: Feed;
  secret: string;
}

/** `LinkStarted`, an api router model with no generated type. */
interface LinkStarted {
  authorization_url: string;
}

export function useAccount(): UseQueryResult<AccountProfile> {
  return useQuery({
    queryKey: accountKeys.me,
    queryFn: () => api.get<AccountProfile>("/v1/accounts/me"),
  });
}

export function useEntitlement(): UseQueryResult<Entitlement> {
  return useQuery({
    queryKey: accountKeys.entitlement,
    queryFn: () => api.get<Entitlement>("/v1/accounts/me/entitlement"),
  });
}

export function useMachines(): UseQueryResult<MachineRecord[]> {
  return useQuery({
    queryKey: accountKeys.machines,
    queryFn: () => api.get<MachineRecord[]>("/v1/accounts/me/machines"),
  });
}

export function useFeeds(): UseQueryResult<Feed[]> {
  return useQuery({
    queryKey: accountKeys.feeds,
    queryFn: async () => {
      const body = await api.get<FeedListResponse>("/v1/accounts/me/feeds");
      return body.feeds ?? [];
    },
  });
}

export function useAuthSessions(): UseQueryResult<AuthSessionSummary[]> {
  return useQuery({
    queryKey: accountKeys.sessions,
    queryFn: () => api.get<AuthSessionSummary[]>("/v1/accounts/me/sessions"),
  });
}

export function useSpacePage(): UseQueryResult<SpacePage> {
  return useQuery({
    queryKey: accountKeys.spaces,
    queryFn: () => spaces.list("active"),
  });
}

export function useCanvasPairings(): UseQueryResult<CanvasPairing[]> {
  return useQuery({ queryKey: accountKeys.canvasPairings, queryFn: canvases.pairings });
}

/** 204 is the answer for nothing loaded, so `undefined` is a state and never a miss. */
export function useLoadedSpace(): UseQueryResult<LoadedSpace | null> {
  return useQuery({
    queryKey: accountKeys.loaded,
    queryFn: async () => {
      const found = await api.get<LoadedSpace | undefined>("/v1/spaces/loaded");
      return found ?? null;
    },
  });
}

export function useRoles(subjectId: string): UseQueryResult<RoleAssignment[]> {
  return useQuery({
    queryKey: accountKeys.roles(subjectId),
    enabled: subjectId !== "",
    queryFn: () => api.get<RoleAssignment[]>(`/v1/accounts/${subjectId}/roles`),
  });
}

export function useRevokeMachine(): UseMutationResult<undefined, Error, string> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (machineId: string) =>
      api.delete<undefined>(`/v1/accounts/me/machines/${machineId}`),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: accountKeys.machines });
    },
  });
}

export function useApprovePairing(): UseMutationResult<PairingView, Error, string> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (userCode: string) =>
      api.post<PairingView>(`/v1/accounts/me/pairings/${encodeURIComponent(userCode)}`),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: accountKeys.machines });
    },
  });
}

export interface RegisterFeedInput {
  provider: string;
  reference: string;
  label: string;
}

export function useRegisterFeed(): UseMutationResult<RegisteredFeed, Error, RegisterFeedInput> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: RegisterFeedInput) =>
      api.post<RegisteredFeed>("/v1/accounts/me/feeds", body),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: accountKeys.feeds });
    },
  });
}

/** One voice room in an installed Discord server, as the picker draws it. */
export interface DiscordVoiceChannel {
  channel_id: string;
  name: string;
}

interface VoiceChannelListResponse {
  channels?: DiscordVoiceChannel[];
}

/** The rooms in the server a Discord feed was installed into. Read on demand, never with the list. */
export function useDiscordVoiceChannels(
  feedId: string | null,
): UseQueryResult<DiscordVoiceChannel[]> {
  return useQuery({
    queryKey: accountKeys.discordChannels(feedId ?? ""),
    enabled: feedId !== null,
    queryFn: async () => {
      const body = await api.get<VoiceChannelListResponse>(
        `/v1/feeds/${feedId ?? ""}/discord/channels`,
      );
      return body.channels ?? [];
    },
  });
}

/** Point one Discord feed at one room. Idempotent, and it cannot move the server. */
export function useChooseDiscordVoiceChannel(): UseMutationResult<
  Feed,
  Error,
  { feedId: string; channelId: string }
> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ feedId, channelId }) =>
      api.put<Feed>(`/v1/feeds/${feedId}/discord/channel`, { channel_id: channelId }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: accountKeys.feeds });
    },
  });
}

export function useSetFeedAuthorization(): UseMutationResult<
  Feed,
  Error,
  { feedId: string; authorized: boolean }
> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ feedId, authorized }) =>
      authorized
        ? api.post<Feed>(`/v1/feeds/${feedId}/authorization`)
        : api.delete<Feed>(`/v1/feeds/${feedId}/authorization`),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: accountKeys.feeds });
    },
  });
}

export interface GrantRoleInput {
  subject_id: string;
  role: Role;
  scope: ScopeRef;
}

export function useGrantRole(): UseMutationResult<RoleAssignment, Error, GrantRoleInput> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: GrantRoleInput) => api.post<RoleAssignment>("/v1/role-assignments", body),
    onSuccess: (_assignment, input) => {
      void client.invalidateQueries({ queryKey: accountKeys.roles(input.subject_id) });
    },
  });
}

export function useRevokeRole(
  subjectId: string,
): UseMutationResult<undefined, Error, string> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (assignmentId: string) =>
      api.delete<undefined>(`/v1/role-assignments/${assignmentId}`),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: accountKeys.roles(subjectId) });
    },
  });
}

export function useSetHandle(): UseMutationResult<AccountProfile, Error, string> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (handle: string) =>
      request<AccountProfile>("/v1/accounts/me/handle", {
        method: "PUT",
        body: JSON.stringify({ handle }),
      }),
    onSuccess: (profile) => {
      client.setQueryData(accountKeys.me, profile);
    },
  });
}

/** `GET /v1/handles/{handle}/availability`, for a field somebody is still typing into.
 *
 * Advisory, always: the answer can go stale between reading it and pressing save, so the write
 * re-decides and this only exists so the field can say "reserved" before it is submitted. */
export function useHandleAvailability(handle: string): UseQueryResult<HandleAvailability> {
  const wanted = handle.trim().toLowerCase();
  return useQuery({
    queryKey: accountKeys.handleAvailability(wanted),
    enabled: wanted.length > 0,
    retry: false,
    staleTime: 10_000,
    queryFn: () =>
      api.get<HandleAvailability>(`/v1/handles/${encodeURIComponent(wanted)}/availability`),
  });
}

export function useStartLink(): UseMutationResult<LinkStarted, Error, string> {
  return useMutation({
    mutationFn: (provider: string) =>
      api.post<LinkStarted>("/v1/accounts/me/links", { provider }),
  });
}

export function useUnlinkProvider(): UseMutationResult<undefined, Error, string> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (provider: string) =>
      api.delete<undefined>(`/v1/accounts/me/links/${encodeURIComponent(provider)}`),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: accountKeys.me });
    },
  });
}

/** A null id ends every other session and leaves this one alive. */
export function useRevokeAuthSession(): UseMutationResult<undefined, Error, string | null> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (authSessionId: string | null) =>
      api.delete<undefined>(
        authSessionId === null
          ? "/v1/accounts/me/sessions"
          : `/v1/accounts/me/sessions/${authSessionId}`,
      ),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: accountKeys.sessions });
    },
  });
}

/** This browser only, with no window in between. */
export function useSignOut(): UseMutationResult<undefined, Error, void> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => api.delete<undefined>("/v1/auth/session"),
    onSuccess: () => {
      void client.invalidateQueries();
    },
  });
}

export function useCreateSpace(): UseMutationResult<unknown, Error, void> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => spaces.create(),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: accountKeys.spaces });
    },
  });
}

/** The canvas is showing a short code; approving binds that device to this account. */
export function useApproveCanvasPairing(): UseMutationResult<unknown, Error, string> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (userCode: string) => canvases.approve(userCode),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: accountKeys.canvasPairings });
    },
  });
}

export function useUnpairCanvas(): UseMutationResult<CanvasPairing, Error, string> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (pairingId: string) => canvases.unpair(pairingId),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: accountKeys.canvasPairings });
    },
  });
}
